<!-- ZayaAI: Case Information panel for an image (LIS case/patient/specimen/slide metadata,
     derived from the image's @lis.* properties via buildCaseInformationModel). Embedded in the
     image detail pop-up. Renders only the sections/fields that have values. -->
<template>
<div class="case-info-panel">
  <b-loading :is-full-page="false" :active="loading" />
  <template v-if="!loading">
    <em v-if="error" class="ci-muted">{{ $t('error-fetch-properties') }}</em>
    <em v-else-if="!model.hasAnyData" class="ci-muted">{{ $t('case-information-empty') }}</em>
    <div v-else class="ci-sections">
      <div v-for="section in visibleSections" :key="section.titleKey" class="ci-section">
        <h4 class="ci-title">{{ $t(section.titleKey) }}</h4>
        <div class="ci-grid">
          <div v-for="field in section.fields" :key="field.labelKey" class="ci-field">
            <span class="ci-l">{{ $t(field.labelKey) }}</span>
            <span class="ci-v">{{ field.value }}</span>
          </div>
        </div>
      </div>
    </div>
  </template>
</div>
</template>

<script>
import {PropertyCollection} from '@/api';
import {buildCaseInformationModel, buildCaseInformationModelFromValues} from '@/utils/case-information';
import {getCaseProps, setCaseProps} from '@/utils/case-info-cache';

export default {
  name: 'case-information-panel',
  props: {
    image: {type: Object, default: null}
  },
  data() {
    return {
      loading: true,
      error: false,
      model: buildCaseInformationModel([])
    };
  },
  computed: {
    visibleSections() {
      return this.model.sections
        .map(section => ({...section, fields: section.fields.filter(f => f.value)}))
        .filter(section => section.fields.length > 0);
    }
  },
  watch: {
    image() {
      this.fetch();
    }
  },
  methods: {
    async fetch() {
      if (!this.image) {
        return;
      }
      // Fast path: the images list already returns the case-info inline (image.caseInfo) — build the
      // panel from it with no network call. Falls back to fetching properties for other callers
      // (e.g. the viewer) that pass an image without caseInfo.
      if (this.image.caseInfo) {
        this.model = buildCaseInformationModelFromValues(this.image.caseInfo);
        this.loading = false;
        this.error = false;
        return;
      }
      this.loading = true;
      this.error = false;
      try {
        // reuse the images-table cache if present, else fetch once and cache for it
        let props = getCaseProps(this.image.id);
        if (!props) {
          props = (await PropertyCollection.fetchAll({object: this.image})).array;
          setCaseProps(this.image.id, props);
        }
        this.model = buildCaseInformationModel(props);
      } catch (error) {
        console.log(error);
        this.error = true;
      }
      this.loading = false;
    }
  },
  created() {
    this.fetch();
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/colors.scss';

.case-info-panel {
  position: relative;
  min-height: 2rem;
}

.ci-muted {
  color: #8a91a1;
  font-style: italic;
}

.ci-section {
  margin-bottom: 1rem;

  &:last-child { margin-bottom: 0; }
}

.ci-title {
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 0.5rem;
}

.ci-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem 1.6rem;

  @media screen and (max-width: 600px) {
    grid-template-columns: 1fr;
  }
}

.ci-field {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-width: 0;
}

.ci-l {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #8a91a1;
}

.ci-v {
  font-size: 0.9rem;
  color: $ink-1;
  word-break: break-word;
}
</style>
